/**
 * /admin/integrations — platform-admin-only page. Currently hosts the
 * user-sync source picker (manual / Slack / Rippling) + a "Sync now"
 * button + run status. Future integrations (Slack notifications,
 * webhook receivers, etc.) slot in as new cards on this same page.
 */

import { useCallback, useEffect, useState } from 'react';
import { api, apiMessage } from '../../api/client.js';
import { AdminTabs } from '../../components/AdminTabs.js';
import { IconBolt, IconShield } from '../../components/Icons.js';
import {
  Banner,
  Button,
  Card,
  CardHeader,
  Field,
  Mono,
  PageHeader,
  PageSkeleton,
  inputClass,
} from '../../components/ui.js';
import { relTime } from '../../lib/format.js';
import { runAction, type ActionMessage } from '../../lib/runAction.js';

type SourceName = 'manual' | 'slack' | 'rippling';

interface SyncCounts {
  scanned: number;
  inserted: number;
  updated: number;
  skipped: number;
  disappeared: number;
  errors: number;
}

interface IntegrationStatus {
  configured: boolean;
  source: SourceName;
  enabled: boolean;
  /** True when the source has encrypted credentials on file. */
  has_credentials: boolean;
  /** Count of user_sync_state rows — how many users are sync-managed today. */
  synced_user_count?: number;
  last_sync_at?: number | null;
  last_sync_status?: 'ok' | 'partial' | 'error' | 'running' | null;
  last_sync_error?: string | null;
  last_sync_counts?: SyncCounts | null;
  updated_at?: number;
}

const SOURCE_OPTIONS: { value: SourceName; label: string; description: string }[] = [
  {
    value: 'manual',
    label: 'Manual',
    description: 'No automatic sync — users are created only on OIDC first-login.',
  },
  {
    value: 'slack',
    label: 'Slack',
    description: 'Pulls active @mytra.ai users from Slack daily, with names + avatars.',
  },
  {
    value: 'rippling',
    label: 'Rippling',
    description: 'Coming later — interface ready; implementation pending.',
  },
];

export function AdminIntegrations() {
  const [status, setStatus] = useState<IntegrationStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<ActionMessage | null>(null);

  const [source, setSource] = useState<SourceName>('manual');
  const [enabled, setEnabled] = useState(false);
  const [slackToken, setSlackToken] = useState('');

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const fresh = await api.get<IntegrationStatus>('/api/admin/integrations/user-sync');
      setStatus(fresh);
      setSource(fresh.source);
      setEnabled(fresh.enabled);
      setError(null);
    } catch (err) {
      setError(apiMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function saveConfig() {
    if (source === 'manual') {
      await runAction({
        do: () =>
          api.put<IntegrationStatus>('/api/admin/integrations/user-sync', {
            source: 'manual',
            enabled: false,
            config: null,
          }),
        onSuccess: () => 'Saved — user-sync set to manual.',
        errorPrefix: "Couldn't save",
        setBusy,
        setMessage,
        reload,
      });
      return;
    }
    if (source === 'slack') {
      // Allow saving without a fresh token only when one is already on file
      // (i.e. the operator is just toggling Enabled). New saves still need
      // a token; the input clears after a save for safety, so re-saving
      // without typing one again is the common case.
      const hasExistingToken = status?.has_credentials === true && status.source === 'slack';
      const tokenChanged = slackToken.length > 0;
      if (tokenChanged && !slackToken.startsWith('xoxb-')) {
        setMessage({ tone: 'error', text: 'Slack bot token must start with "xoxb-".' });
        return;
      }
      if (!tokenChanged && !hasExistingToken) {
        setMessage({ tone: 'error', text: 'Paste a Slack bot token to save.' });
        return;
      }
      await runAction({
        do: () =>
          api.put<IntegrationStatus>('/api/admin/integrations/user-sync', {
            source: 'slack',
            enabled,
            // When token blank + existing on file, send a config object
            // that the server can refuse-and-keep — currently the route
            // requires a botToken so we pass through unchanged when the
            // operator typed nothing.
            config: tokenChanged ? { source: 'slack', botToken: slackToken } : undefined,
          }),
        onSuccess: () => {
          setSlackToken('');
          return enabled
            ? 'Saved. Click "Sync now" to populate users.'
            : 'Saved (disabled — no automatic sync will run).';
        },
        errorPrefix: "Couldn't save",
        setBusy,
        setMessage,
        reload,
      });
      return;
    }
    setMessage({ tone: 'warn', text: 'Rippling source is not implemented yet.' });
  }

  async function syncNow() {
    await runAction({
      do: () => api.post<{ counts: SyncCounts }>('/api/admin/integrations/user-sync/run'),
      onSuccess: (res) =>
        `Sync ok — scanned ${res.counts.scanned}, inserted ${res.counts.inserted}, ` +
        `updated ${res.counts.updated}, disappeared ${res.counts.disappeared}, errors ${res.counts.errors}.`,
      errorPrefix: "Couldn't sync",
      setBusy,
      setMessage,
      reload,
    });
  }

  if (loading && !status) return <PageSkeleton rows={4} />;

  return (
    <div className="animate-fade-in">
      <PageHeader
        eyebrow="Admin"
        title="Integrations"
        description="Platform-wide integration settings. Right now: which directory feeds the Kin users table + avatars."
      />

      <AdminTabs />

      {error && <Banner tone="error">{error}</Banner>}
      {message && (
        <Banner tone={message.tone} onDismiss={() => setMessage(null)}>
          {message.text}
        </Banner>
      )}

      <Card>
        <CardHeader
          title="User directory sync"
          description="Pre-populates Kin's users table so creators can share apps with anyone, even people who haven't logged in yet. Sync runs daily at 03:00 UTC plus on demand."
        />
        <div className="px-5 py-4 space-y-5">
          <Banner tone="info">
            <span className="inline-flex items-center gap-1.5">
              <IconShield size={14} />
              Credentials are encrypted at rest under the platform master key. They never travel
              back to the browser after saving.
            </span>
          </Banner>

          <Field label="Source">
            <select
              value={source}
              onChange={(e) => setSource(e.target.value as SourceName)}
              className={inputClass}
            >
              {SOURCE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted mt-1">
              {SOURCE_OPTIONS.find((o) => o.value === source)?.description}
            </p>
          </Field>

          {source === 'slack' && (
            <>
              <Field
                label={
                  <span className="inline-flex items-center gap-2">
                    Slack bot token
                    {status?.has_credentials && status.source === 'slack' && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-brand-soft text-brand font-medium">
                        ✓ on file
                      </span>
                    )}
                  </span>
                }
              >
                <input
                  type="password"
                  value={slackToken}
                  onChange={(e) => setSlackToken(e.target.value)}
                  placeholder={
                    status?.has_credentials && status.source === 'slack'
                      ? 'Leave blank to keep the existing token'
                      : 'xoxb-…'
                  }
                  autoComplete="off"
                  className={`${inputClass} font-mono`}
                />
                <p className="text-xs text-muted mt-1">
                  Scopes <Mono>users:read</Mono> + <Mono>users:read.email</Mono>. The token is
                  encrypted on save; it never travels back to the browser.
                </p>
              </Field>
              <Field label="Enabled">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={enabled}
                    onChange={(e) => setEnabled(e.target.checked)}
                  />
                  Run daily and accept manual sync triggers.
                </label>
              </Field>
            </>
          )}

          {source === 'rippling' && (
            <Banner tone="warn">
              Rippling source is on the roadmap. The interface is wired and a future drop-in can add
              it without portal changes.
            </Banner>
          )}

          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              size="md"
              loading={busy}
              disabled={busy}
              onClick={() => void saveConfig()}
            >
              Save configuration
            </Button>
            <Button
              variant="ghost"
              size="md"
              disabled={busy || !status?.enabled || status?.source === 'manual'}
              onClick={() => void syncNow()}
            >
              <IconBolt size={13} />
              Sync now
            </Button>
          </div>
        </div>

        {status?.configured && (
          <div className="border-t border-line px-5 py-4 grid sm:grid-cols-2 gap-3 text-sm">
            <StatusRow label="Active source" value={status.source} />
            <StatusRow label="Enabled" value={status.enabled ? 'yes' : 'no'} />
            <StatusRow
              label="Credentials"
              value={status.has_credentials ? 'on file' : 'not saved'}
            />
            <StatusRow label="Sync-managed users" value={String(status.synced_user_count ?? 0)} />
            <StatusRow
              label="Last run"
              value={status.last_sync_at ? relTime(status.last_sync_at) : 'never'}
            />
            <StatusRow label="Last status" value={status.last_sync_status ?? '—'} />
            {status.last_sync_error && (
              <div className="sm:col-span-2 text-danger text-xs">
                Error: {status.last_sync_error}
              </div>
            )}
            {status.last_sync_counts && (
              <div className="sm:col-span-2 text-xs text-muted">
                Last counts — scanned {status.last_sync_counts.scanned}, inserted{' '}
                {status.last_sync_counts.inserted}, updated {status.last_sync_counts.updated},
                disappeared {status.last_sync_counts.disappeared}, errors{' '}
                {status.last_sync_counts.errors}
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}

function StatusRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted text-xs uppercase tracking-wider">{label}</span>
      <Mono className="text-sm">{value}</Mono>
    </div>
  );
}
